import { FC, useState } from 'react';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import { useIntl, defineMessages } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link';

import EmailIcon from 'dibs-icons/exports/legacy/Email';
import { PdpSharedSendToFriendModalLazy } from '../../PdpSharedModals/PdpSharedSendToFriendModal/PdpSharedSendToFriendModalLazy';

// helpers
import { trackEmailClick } from '../../tracking/iconBubblesTracking';

import { SocialShareVariant } from './types';
import { PdpSharedSocialShareEmail_viewer$data } from './__generated__/PdpSharedSocialShareEmail_viewer.graphql';
import { PdpSharedSocialShareEmail_item$data } from './__generated__/PdpSharedSocialShareEmail_item.graphql';
import { PdpSharedSocialShareEmail_user$data } from './__generated__/PdpSharedSocialShareEmail_user.graphql';

// styles
import styles from './PdpSharedSocialShare.scss';

const messages = defineMessages({
    label: {
        id: 'PdpSharedSocialShareEmail.label',
        defaultMessage: 'Share {title} by email',
    },
});

type State = {
    modalOpen: boolean;
    modalState: null | 'SUCCESS' | 'ERROR';
    isLoading: boolean;
    localSenderEmail: string;
    isRecentlySignedUpUser: boolean;
};

type Props = {
    viewer: PdpSharedSocialShareEmail_viewer$data;
    item: PdpSharedSocialShareEmail_item$data;
    user: PdpSharedSocialShareEmail_user$data | null;
    skuId?: string | null;
} & SocialShareVariant;

const updateState =
    (update: Partial<State>) =>
    (state: State): State => ({
        ...state,
        ...update,
    });

const PdpSharedSocialShareEmailComponent: FC<Props> = ({
    item,
    skuId,
    user,
    viewer,
    variant = 'iconBubble',
    label,
}) => {
    const { formatMessage } = useIntl();
    const [state, setState] = useState<State>({
        modalOpen: false,
        modalState: null,
        isLoading: false,
        localSenderEmail: '',
        isRecentlySignedUpUser: false,
    });

    const { isPdpStfEnabled } = viewer;

    if (!isPdpStfEnabled) {
        return null;
    }

    const openModal = (): void => {
        setState(updateState({ modalOpen: true }));
    };

    const closeModal = (): void =>
        setState(
            updateState({
                modalOpen: false,
                modalState: null,
                isRecentlySignedUpUser: false,
            })
        );

    const setSuccess = (): void => setState(updateState({ modalState: 'SUCCESS' }));
    const setError = (): void => setState(updateState({ modalState: 'ERROR' }));

    const setIsLoading = (isLoading: boolean): void => setState(updateState({ isLoading }));

    const onClick = (): void => {
        openModal();
        trackEmailClick(item.serviceId);
    };

    const onUserLogin = (localSenderEmail: string, isRecentlySignedUpUser: boolean): void => {
        setState(
            updateState({
                localSenderEmail,
                isRecentlySignedUpUser,
            })
        );
        openModal();
    };

    const { modalOpen, modalState, localSenderEmail, isLoading, isRecentlySignedUpUser } = state;

    let imageSrc = item.firstPhotoWebPath;

    if (skuId) {
        const sku = (item.skus || []).find(s => s?.serviceId === skuId);
        const skuPhoto = sku?.firstPhotoWebPath;
        imageSrc = skuPhoto ? skuPhoto : imageSrc;
    }
    const senderEmail = user?.profile?.email ?? localSenderEmail;
    const isModalOpen = modalOpen || !!modalState;

    return (
        <Link
            className={styles[`${variant}Variant`]}
            ariaLabel={formatMessage(messages.label, { title: item.title })}
            onClick={onClick}
            role="menuitem"
        >
            <EmailIcon className={styles.icon} />
            {label && <span>{label}</span>}
            <PdpSharedSendToFriendModalLazy
                key={senderEmail}
                type="PDP"
                modalOpen={isModalOpen}
                hasError={modalState === 'ERROR'}
                hasSuccess={modalState === 'SUCCESS'}
                setError={setError}
                setSuccess={setSuccess}
                senderEmail={senderEmail}
                imageSrc={imageSrc || ''}
                skuId={skuId ?? undefined}
                closeModal={closeModal}
                onUserLogin={onUserLogin}
                isRecentlySignedUpUser={isRecentlySignedUpUser}
                setIsLoading={setIsLoading}
                isLoading={isLoading}
                user={user}
                item={item}
                viewer={viewer}
                isSubmitInitiallyEnabled
            />
        </Link>
    );
};

export const PdpSharedSocialShareEmail = createFragmentContainer(
    PdpSharedSocialShareEmailComponent,
    {
        viewer: graphql`
            fragment PdpSharedSocialShareEmail_viewer on Viewer {
                ... @include(if: $fetchPdp) {
                    isPdpStfEnabled: featureFlag(feature: "enablePdpStf")
                    ...PdpSharedSendToFriendModalLazy_viewer
                }
            }
        `,
        item: graphql`
            fragment PdpSharedSocialShareEmail_item on Item {
                serviceId
                title
                firstPhotoWebPath(size: small)
                skus {
                    serviceId
                    firstPhotoWebPath(size: small)
                }
                ...PdpSharedSendToFriendModalLazy_item
            }
        `,
        user: graphql`
            fragment PdpSharedSocialShareEmail_user on User {
                profile {
                    email
                }
                ...PdpSharedSendToFriendModalLazy_user
            }
        `,
    }
);
